/**
 * Work Centers Management Page
 *
 * Manage work centers (machines/equipment) used in production scheduling.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  RefreshCw,
  Search,
  Loader2,
  Factory,
  Edit,
  Trash2,
  X,
  Cog,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import productionService from '../services/productionService';

const TYPE_LABELS = {
  cnc: '数控机床',
  lathe: '车床',
  grinding: '磨床',
  assembly: '装配',
  inspection: '检测',
  packaging: '包装',
  other: '其他',
};

const STATUS_LABELS = {
  active: '正常运行',
  maintenance: '维护中',
  inactive: '停用',
};

const STATUS_VARIANTS = {
  active: 'default',
  maintenance: 'secondary',
  inactive: 'destructive',
};

function WorkCentersPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [workCenters, setWorkCenters] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWorkCenter, setEditingWorkCenter] = useState(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('cnc');
  const [formDescription, setFormDescription] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formCapacity, setFormCapacity] = useState('10');
  const [formDailyCapacity, setFormDailyCapacity] = useState('');
  const [formCost, setFormCost] = useState('0');
  const [formStatus, setFormStatus] = useState('active');

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  const loadWorkCenters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, page_size: pageSize };
      if (searchTerm) params.search = searchTerm;
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const response = await productionService.getWorkCenters(params);
      setWorkCenters(response.items || []);
      setTotalItems(response.total || 0);
    } catch (err) {
      console.error('Failed to load work centers:', err);
      setError('加载加工中心列表失败，请重试。');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, typeFilter, statusFilter]);

  useEffect(() => {
    loadWorkCenters();
  }, [loadWorkCenters, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleTypeFilter = (value) => {
    setTypeFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value === 'all' ? '' : value);
    setPage(1);
  };

  const resetForm = () => {
    setFormName('');
    setFormType('cnc');
    setFormDescription('');
    setFormLocation('');
    setFormCapacity('10');
    setFormDailyCapacity('');
    setFormCost('0');
    setFormStatus('active');
    setDialogError(null);
  };

  const openCreateDialog = () => {
    setEditingWorkCenter(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (wc) => {
    setEditingWorkCenter(wc);
    setFormName(wc.name || '');
    setFormType(wc.type || 'cnc');
    setFormDescription(wc.description || '');
    setFormLocation(wc.location || '');
    setFormCapacity(String(wc.capacity_per_hour ?? 10));
    setFormDailyCapacity(wc.daily_capacity != null ? String(wc.daily_capacity) : '');
    setFormCost(String(wc.cost_per_hour ?? 0));
    setFormStatus(wc.status || 'active');
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setDialogError('名称不能为空');
      return;
    }
    const data = {
      name: formName.trim(),
      type: formType,
      description: formDescription.trim(),
      location: formLocation.trim(),
      capacity_per_hour: parseFloat(formCapacity) || 10,
      daily_capacity: formDailyCapacity.trim() ? parseFloat(formDailyCapacity) : null,
      cost_per_hour: parseFloat(formCost) || 0,
      status: formStatus,
    };
    try {
      setDialogLoading(true);
      if (editingWorkCenter) {
        await productionService.updateWorkCenter(editingWorkCenter._id, data);
      } else {
        await productionService.createWorkCenter(data);
      }
      setDialogOpen(false);
      handleRefresh();
    } catch (err) {
      setDialogError(err.response?.data?.error || err.message || '保存失败');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async (wc) => {
    if (!window.confirm(`确定要删除加工中心 "${wc.name}" 吗？`)) {
      return;
    }
    try {
      setLoading(true);
      await productionService.deleteWorkCenter(wc._id);
      handleRefresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  const canEdit = user && ['admin', 'warehouse_manager'].includes(user.role);

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Layout activeTab="settings">
      <div className="container mx-auto py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">加工中心管理</h1>
            <p className="text-muted-foreground">管理生产设备与加工中心配置</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              刷新
            </Button>
            {canEdit && (
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                新增加工中心
              </Button>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="搜索编号或名称..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={typeFilter || 'all'} onValueChange={handleTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="按类型筛选" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部类型</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="按状态筛选" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部状态</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="h-5 w-5" />
              加工中心列表 ({totalItems})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : workCenters.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                未找到加工中心。{canEdit && '点击"新增加工中心"添加。'}
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>编号</TableHead>
                      <TableHead>名称</TableHead>
                      <TableHead>类型</TableHead>
                      <TableHead>位置</TableHead>
                      <TableHead>产能(件/时)</TableHead>
                      <TableHead>单机日产能</TableHead>
                      <TableHead>成本(元/时)</TableHead>
                      <TableHead>状态</TableHead>
                      <TableHead className="text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workCenters.map((wc) => (
                      <TableRow key={wc._id}>
                        <TableCell className="font-mono text-xs">{wc.work_center_id}</TableCell>
                        <TableCell>
                          <div className="font-medium">{wc.name}</div>
                          {wc.description && (
                            <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {wc.description}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{TYPE_LABELS[wc.type] || wc.type}</Badge>
                        </TableCell>
                        <TableCell>{wc.location || '-'}</TableCell>
                        <TableCell>{wc.capacity_per_hour ?? '-'}</TableCell>
                        <TableCell>{wc.daily_capacity != null ? wc.daily_capacity : '-'}</TableCell>
                        <TableCell>{wc.cost_per_hour ? `¥${wc.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANTS[wc.status] || 'outline'}>
                            {STATUS_LABELS[wc.status] || wc.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {canEdit && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(wc)}
                                  title="编辑"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(wc)}
                                  className="text-destructive hover:text-destructive"
                                  title="删除"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4">
                    <div className="text-sm text-muted-foreground">
                      第 {page} 页 / 共 {totalPages} 页（共 {totalItems} 项）
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        上一页
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Cog className="h-5 w-5" />
                {editingWorkCenter ? '编辑加工中心' : '新增加工中心'}
              </DialogTitle>
              <DialogDescription>
                {editingWorkCenter
                  ? `编辑 ${editingWorkCenter.name}`
                  : '填写加工中心信息，系统将自动生成编号。'}
              </DialogDescription>
            </DialogHeader>

            {dialogError && (
              <Alert variant="destructive" className="mt-2">
                <AlertDescription>{dialogError}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="wc-name" className="text-right">名称</Label>
                <Input
                  id="wc-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="如：数控车床-01"
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="wc-type" className="text-right">类型</Label>
                <Select value={formType} onValueChange={setFormType}>
                  <SelectTrigger id="wc-type" className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="wc-status" className="text-right">状态</Label>
                <Select value={formStatus} onValueChange={setFormStatus}>
                  <SelectTrigger id="wc-status" className="col-span-3">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="wc-location" className="text-right">位置</Label>
                <Input
                  id="wc-location"
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                  placeholder="如：车间A-01号位"
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="wc-capacity" className="text-right">产能(件/时)</Label>
                <Input
                  id="wc-capacity"
                  type="number"
                  min="0"
                  step="1"
                  value={formCapacity}
                  onChange={(e) => setFormCapacity(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="wc-daily-capacity" className="text-right">单机日产能</Label>
                <Input
                  id="wc-daily-capacity"
                  type="number"
                  min="0"
                  step="1"
                  value={formDailyCapacity}
                  onChange={(e) => setFormDailyCapacity(e.target.value)}
                  placeholder="留空则使用 产能×8"
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="wc-cost" className="text-right">成本(元/时)</Label>
                <Input
                  id="wc-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formCost}
                  onChange={(e) => setFormCost(e.target.value)}
                  className="col-span-3"
                />
              </div>
              <div className="grid grid-cols-4 items-start gap-4">
                <Label htmlFor="wc-desc" className="text-right pt-2">描述</Label>
                <Input
                  id="wc-desc"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="可选描述..."
                  className="col-span-3"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button onClick={handleSave} disabled={dialogLoading}>
                {dialogLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                保存
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default WorkCentersPage;
